import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Check, X, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../Button';
import {
  verifyConnection,
  type OnboardingSession,
  type VerifyResult,
} from '../../lib/queries/onboarding';

export default function Step3Verify({
  session,
  onBack,
}: {
  session: OnboardingSession;
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const mutation = useMutation<VerifyResult>({
    mutationFn: () => verifyConnection(session.tenantId),
  });

  const result = mutation.data;

  return (
    <div className="bg-bg-elevated py-4">
      <div className="mb-6 px-4">
        <div className="mb-6">
          <h2 className="text-primary text-base font-semibold">
            Verify connection
          </h2>
          <p className="text-muted mt-1 text-sm">
            {mutation.isPending
              ? "Running end-to-end checks against the tenant's NetSuite account…"
              : 'Run checks to confirm the SuiteApp is installed and minting valid identity tokens.'}
          </p>
        </div>

        {!result && (
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="mb-6"
          >
            {mutation.isPending ? 'Running checks…' : 'Run verification'}
          </Button>
        )}

        {result && (
          <div className="mb-6 space-y-2">
            {result.checks.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-3 rounded-lg border border-white/8 px-4 py-3"
              >
                <div
                  className={[
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                    c.passed ? 'bg-success' : 'bg-danger',
                  ].join(' ')}
                >
                  {c.passed ? (
                    <Check size={12} className="text-white" />
                  ) : (
                    <X size={12} className="text-white" />
                  )}
                </div>
                <div>
                  <p className="text-primary text-sm font-medium">{c.label}</p>
                  <p className="text-muted text-xs">{c.detail}</p>
                </div>
              </div>
            ))}

            {result.allPassed ? (
              <div className="flex items-center gap-3 rounded-lg border border-[--color-success]/30 bg-[--color-success]/8 px-4 py-3">
                <Check size={16} className="shrink-0 text-[--color-success]" />
                <div>
                  <p className="text-sm font-semibold text-[--color-success]">
                    All checks passed · Tenant is live
                  </p>
                  <p className="text-muted text-xs">
                    Tenant activated — it now appears in the tenant list as
                    Active.
                  </p>
                </div>
              </div>
            ) : (
              <Button
                variant="secondary"
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Re-checking…' : 'Re-run verification'}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-4 border-t border-white/8 px-4 pt-4">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeft size={14} /> Back
        </Button>
        <Button
          onClick={() => navigate({ to: '/' })}
          disabled={!result?.allPassed}
        >
          Done - View tenants <ArrowRight size={14} />
        </Button>
      </div>
    </div>
  );
}
